import React from 'react';
import { Dimensions, ScrollView, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { toHtmlTextStyle } from '../components/general/toHtmlTextStyle';

const WIDTH = Dimensions.get('window').width;
const PAGE_HORIZONTAL_PADDING = WIDTH >= 1200 ? 100 : 24;

const policySections = [
    {
        title: '1) Information we collect',
        points: [
            'Account and profile details, such as your name, email, phone number, role (parent or camp owner), and account preferences.',
            'Camp and booking information, including camp listings, availability, registration records, communications, and support requests.',
            'Device and usage data, such as browser type, app version, pages viewed, referring links, crash and diagnostic signals, and approximate location from IP.',
            'Payment-related information processed by trusted payment providers. SnapCamp does not store full payment card numbers.',
        ],
    },
    {
        title: '2) How we use information',
        points: [
            'Operate, personalize, and improve SnapCamp services for parents, camp operators, and program participants.',
            'Process bookings, registrations, account administration, and customer support interactions.',
            'Protect platform integrity, prevent fraud, enforce our terms, and investigate safety or policy concerns.',
            'Send service-related updates, confirmations, security notices, and product announcements.',
        ],
    },
    {
        title: '3) How information is shared',
        points: [
            'With camps, parents, and authorized users when needed to deliver bookings, registrations, and platform operations.',
            'With vendors and service providers that support hosting, analytics, payments, communications, and customer support.',
            'With regulators, law enforcement, or legal advisors when required by law or to protect rights, safety, and trust.',
            'During a business transaction (for example, merger, acquisition, or asset transfer), subject to applicable legal safeguards.',
        ],
    },
    {
        title: '4) Your choices and rights',
        points: [
            'You may access, correct, or update account data by using account settings or contacting us.',
            'You may request deletion of your account and personal information, subject to legal, safety, and contractual retention requirements.',
            'You may opt out of marketing communications, while still receiving essential service and security notices.',
            'Where required by law, you may exercise applicable privacy rights, including rights to access, portability, objection, or restriction.',
        ],
    },
    {
        title: '5) Data retention',
        points: [
            'We retain information as long as needed for service delivery, safety, dispute resolution, legal compliance, and business records.',
            'Retention periods vary by data type, account activity, legal obligations, and legitimate operational needs.',
        ],
    },
    {
        title: '6) Data security',
        points: [
            'We use administrative, technical, and organizational safeguards designed to protect personal information.',
            'No system is completely secure, but we continuously work to improve protections and incident response practices.',
        ],
    },
    {
        title: '7) International data transfers',
        points: [
            'SnapCamp and our providers may process information in different jurisdictions.',
            'When data is transferred across borders, we apply safeguards appropriate to applicable privacy laws.',
        ],
    },
    {
        title: '8) Children and youth privacy',
        points: [
            'SnapCamp is intended for use by parents, guardians, camp operators, and authorized staff, not for direct use by young children.',
            'If you believe personal information about a child was provided inappropriately, contact us so we can review and address the request promptly.',
        ],
    },
    {
        title: '9) Mobile app permissions and app stores',
        points: [
            'Our mobile apps may request permissions (for example, camera, photos, notifications, or location) only when needed for specific features.',
            'You can manage app permissions in your device settings at any time.',
            'This policy is intended to support public privacy disclosures for iOS App Store and Google Play submissions.',
        ],
    },
    {
        title: '10) Contact us',
        points: [
            'For privacy questions or requests, contact: privacy@snapcamp.ai',
            'For general support, visit the Help Center or Contact Us pages.',
        ],
    },
];

const PrivacyPolicyScreen = () => {
    return (
        <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
            <View style={styles.container}>
                <View style={styles.hero}>
                    <View style={styles.badge}>
                        <MaterialCommunityIcons name="shield-lock-outline" size={16} color="#1D4ED8" />
                        <Text style={styles.badgeText}>Privacy Policy</Text>
                    </View>

                    <h1 style={toHtmlTextStyle(styles.heroTitle, { marginTop: 0 })}>How SnapCamp collects, uses, and protects personal information.</h1>
                    <Text style={styles.heroSubtitle}>
                        This Privacy Policy describes our privacy practices for SnapCamp web and mobile applications. It is written to be clear for families, camp owners, and app-store review teams.
                    </Text>

                    <View style={styles.metaRow}>
                        <Text style={styles.metaText}>Effective date: April 2, 2026</Text>
                        <Text style={styles.metaDot}>•</Text>
                        <Text style={styles.metaText}>Last updated: April 2, 2026</Text>
                    </View>
                </View>

                <View style={styles.sectionList}>
                    {policySections.map((section) => (
                        <View key={section.title} style={styles.sectionCard}>
                            <Text style={styles.sectionTitle}>{section.title}</Text>
                            <View style={styles.bulletList}>
                                {section.points.map((point) => (
                                    <View key={point} style={styles.bulletRow}>
                                        <View style={styles.bulletDot} />
                                        <Text style={styles.bulletText}>{point}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    ))}
                </View>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#F8FAFC',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 72,
        paddingHorizontal: PAGE_HORIZONTAL_PADDING,
    },
    container: {
        width: '100%',
        gap: 24,
    },
    hero: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    badge: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        backgroundColor: '#DBEAFE',
        marginBottom: 18,
    },
    badgeText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#1D4ED8',
    },
    heroTitle: {
        fontSize: 36,
        lineHeight: 44,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 14,
    },
    heroSubtitle: {
        fontSize: 17,
        lineHeight: 28,
        color: '#475569',
        marginBottom: 16,
    },
    metaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
    },
    metaText: {
        fontSize: 13,
        lineHeight: 20,
        color: '#64748B',
        fontWeight: '600',
    },
    metaDot: {
        fontSize: 14,
        color: '#94A3B8',
    },
    sectionList: {
        gap: 16,
    },
    sectionCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 24,
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    sectionTitle: {
        fontSize: 22,
        lineHeight: 30,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 14,
    },
    bulletList: {
        gap: 12,
    },
    bulletRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
    },
    bulletDot: {
        width: 8,
        height: 8,
        borderRadius: 999,
        marginTop: 8,
        backgroundColor: '#2563EB',
    },
    bulletText: {
        flex: 1,
        fontSize: 15,
        lineHeight: 24,
        color: '#334155',
    },
});

export default PrivacyPolicyScreen;
